//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Abortable Multi-Fetch Composable
 *
 * Provides utilities for managing multiple concurrent AbortController instances
 * with automatic cleanup on component unmount.
 *
 * Responsibilities:
 * - Track multiple active AbortControllers in a Set
 * - Wrap async operations with abort signal support
 * - Return null on abort instead of throwing (graceful handling)
 * - Abort all pending requests on component unmount
 * - Support bulk abort for cleanup scenarios (logout, navigation, etc.)
 *
 * Use Cases:
 * - API composables that fetch from multiple endpoints
 * - Preventing memory leaks from abandoned requests
 * - Graceful request cancellation on component unmount
 * - Avoiding race conditions when fetching with outdated parameters
 */
export function useAbortableMultiFetch() {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const controllers: Set<AbortController> = new Set();

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Wrap an async operation with AbortController
   *
   * Purpose:
   * Execute async function with abort signal and automatic controller cleanup.
   * Returns null on abort to enable graceful handling without throwing.
   *
   * Responsibilities:
   * - Create and register new AbortController
   * - Pass abort signal to provided function
   * - Return null if request is aborted (no error throw)
   * - Re-throw non-abort errors
   * - Cleanup controller from tracking Set on completion
   *
   * @param fn - Async function that accepts AbortSignal
   * @returns Result of function or null if aborted
   */
  const withAbort = async <T>(fn: (signal: AbortSignal) => Promise<T>): Promise<T | null> => {
    const controller = new AbortController();
    controllers.add(controller);

    try {
      return await fn(controller.signal);
    } catch (err: any) {
      if (err.name === "AbortError" || controller.signal.aborted) {
        return null;
      }
      throw err;
    } finally {
      controllers.delete(controller);
    }
  };

  /**
   * Abort all tracked requests
   *
   * Purpose:
   * Cancel all pending requests and clear controller tracking.
   * Used for cleanup on logout, navigation, or component unmount.
   *
   * Responsibilities:
   * - Abort all registered AbortControllers
   * - Clear the tracking Set
   * - Trigger \"AbortError\" in all pending withAbort calls
   */
  const abortAll = () => {
    controllers.forEach((c) => c.abort());
    controllers.clear();
  };

  // Only attach lifecycle hook if inside a component setup
  if (getCurrentInstance()) {
    onUnmounted(abortAll);
  }

  return { withAbort, abortAll };
}
